import type { Bounds, Vec2 } from '$lib/core/geom';
import { vec } from '$lib/core/geom';

export interface Viewport {
  readonly center: Vec2;
  readonly zoom: number;
  readonly width: number;
  readonly height: number;
}

export const DEFAULT_VIEWPORT: Viewport = {
  center: vec(0, 0),
  zoom: 4,
  width: 800,
  height: 600,
};

/** World units per screen pixel. */
export const pixelSize = (viewport: Viewport): number => 1 / viewport.zoom;

export function toScreen(viewport: Viewport, world: Vec2): Vec2 {
  return vec(
    (world.x - viewport.center.x) * viewport.zoom + viewport.width / 2,
    viewport.height / 2 - (world.y - viewport.center.y) * viewport.zoom,
  );
}

export function toWorld(viewport: Viewport, screen: Vec2): Vec2 {
  return vec(
    (screen.x - viewport.width / 2) / viewport.zoom + viewport.center.x,
    (viewport.height / 2 - screen.y) / viewport.zoom + viewport.center.y,
  );
}

export function resize(viewport: Viewport, width: number, height: number): Viewport {
  return { ...viewport, width, height };
}

export function panByScreen(viewport: Viewport, dx: number, dy: number): Viewport {
  return {
    ...viewport,
    center: vec(viewport.center.x - dx / viewport.zoom, viewport.center.y + dy / viewport.zoom),
  };
}

export function zoomAt(viewport: Viewport, screenPoint: Vec2, factor: number): Viewport {
  const zoom = clampZoom(viewport.zoom * factor);
  const before = toWorld(viewport, screenPoint);
  const after = toWorld({ ...viewport, zoom }, screenPoint);
  return {
    ...viewport,
    zoom,
    center: vec(viewport.center.x + (before.x - after.x), viewport.center.y + (before.y - after.y)),
  };
}

export function fitBounds(viewport: Viewport, bounds: Bounds, padding = 40): Viewport {
  const width = Math.max(bounds.max.x - bounds.min.x, 1e-6);
  const height = Math.max(bounds.max.y - bounds.min.y, 1e-6);
  const zoom = clampZoom(
    Math.min((viewport.width - padding * 2) / width, (viewport.height - padding * 2) / height),
  );
  return {
    ...viewport,
    zoom,
    center: vec((bounds.min.x + bounds.max.x) / 2, (bounds.min.y + bounds.max.y) / 2),
  };
}

export const clampZoom = (zoom: number): number => Math.min(1e5, Math.max(1e-4, zoom));
